import type { AdminResponse } from "../../lib/api";
import { SURVEY_QUESTIONS, type Question } from "../../lib/survey-config";

function answerOf(r: AdminResponse, key: string): unknown {
  return (r.answers as unknown as Record<string, unknown>)[key];
}

function lookupOptionLabel(q: Question, value: string): string {
  if (q.type !== "singleSelect" && q.type !== "multiSelect") return value;
  return q.options.find((o) => o.value === value)?.label ?? value;
}

export function CompareView({ a, b }: { a: AdminResponse; b: AdminResponse }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 border-b border-border pb-3 text-sm font-mono">
        <div>{a.participant}</div>
        <div>{b.participant}</div>
      </div>
      {SURVEY_QUESTIONS.map((q, i) => (
        <div key={q.id} className="space-y-2">
          <h3 className="text-xs font-mono uppercase tracking-wider text-fg-muted">
            F{i + 1} · {q.id}
          </h3>
          <p className="text-sm font-medium leading-snug">{q.title}</p>
          <div className="grid grid-cols-2 gap-4">
            <CompareCell q={q} response={a} />
            <CompareCell q={q} response={b} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CompareCell({ q, response }: { q: Question; response: AdminResponse }) {
  const v = answerOf(response, q.id);
  return (
    <div className="rounded-input border border-border bg-bg-elevated p-3 text-sm">
      {renderCompareAnswer(q, v, response)}
    </div>
  );
}

function renderCompareAnswer(q: Question, value: unknown, response: AdminResponse) {
  if (q.type === "shortText" || q.type === "longText") {
    if (typeof value !== "string" || !value.trim()) {
      return <span className="italic text-fg-muted">—</span>;
    }
    return <span className="whitespace-pre-wrap">{value}</span>;
  }
  if (q.type === "singleSelect") {
    if (typeof value !== "string" || !value) {
      return <span className="italic text-fg-muted">—</span>;
    }
    return <span>{lookupOptionLabel(q, value)}</span>;
  }
  if (q.type === "multiSelect") {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    const other = answerOf(response, `${q.id}_other`);
    return (
      <div className="space-y-1">
        {arr.length === 0 ? (
          <span className="italic text-fg-muted">—</span>
        ) : (
          <ul className="list-disc pl-5">
            {arr.map((v) => (
              <li key={v}>{lookupOptionLabel(q, v)}</li>
            ))}
          </ul>
        )}
        {q.allowOther && typeof other === "string" && other.trim() ? (
          <div className="text-xs text-fg-muted">+ {other}</div>
        ) : null}
      </div>
    );
  }
  if (q.type === "slider") {
    if (typeof value !== "number") return <span className="italic text-fg-muted">—</span>;
    return (
      <span>
        <strong className="text-accent">{value}</strong>
        <span className="text-fg-muted"> / {q.max}</span>
      </span>
    );
  }
  return null;
}
